import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { makeAutoObservable, toJS } from 'mobx';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { GridRowParams } from '@material-ui/data-grid';
import QrCodeIcon from '@material-ui/icons/QrCode';
import Radio from '@mui/material/Radio';
import moment from 'moment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CONNECTOR_STATUS } from '../../types/Connector';
import { getCommonParams, useAccountFilter } from '../Analytics/useAnalyticsParams';
import { useStyles } from '../../styles/styles';
import useConnectorStatusStyles from '../../styles/connectorStatusStyles';
import { FormInputTypes } from '../../utils/constants';
import { drawerStore } from '../../state/drawerStore';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { CHARGER_STATUS } from '../../types/Charger';
import { ColumnStatusBadge, ConnectorStatusBadge } from '../../components/other/CellBadges';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import StyledCardContent from '../../components/cards/StyledCardContent';
import VMDataGrid from '../../components/main/VMDataGrid';
import { ConnectorColumnCell, formatToSloDate } from '../Sessions/SessionsPage';
import ActionsList from '../../components/other/ActionsList';
import { toastStore, TOAST_MESSAGE_TYPE } from '../../state/toastStore';
import { gridColumn } from '../../utils/gridUtils';
import { getAssignChargerCombinedInputs } from './UnassignedChargers';
import ChargerNapSiSettingsCard from './ChargerNapSiSettingsCard';
import {
  assignToAccountInputs,
  getPriceListDropdownInput,
} from '../Locations/LocationDetailsOverview';
import { NameValueRowList } from '../../components/cards/CardRows/NameValueRowList';
import { useProvider } from '../../state/providerStore';
import { Api } from '../../api';
import NameValueRow from '../../components/cards/CardRows/NameValueRow';
import { useCardStyles } from '../../styles/useCardStyles';
import { companyDetailsParameters, priceListDetailsRoute } from '../../utils/routing';
import { copyTextToClipboard } from '../../utils/utils';
import { greenTextColor } from '../../components/main/react/App';
import { TitleSubtitleRow } from '../../components/cards/CardRows/TitleSubtitleRow';
import credentialStore from '../../state/credentialStore';
import { ReactComponent as UserBased } from '../../images/CatalogRules/user_based.svg';
import { ReactComponent as Timebased } from '../../images/CatalogRules/time_based.svg';
import { ReactComponent as Free } from '../../images/CatalogRules/free.svg';
import { ReactComponent as Inherited } from '../../images/CatalogRules/inherited.svg';

export const UserBasedRuleIcon = () => (
  <UserBased style={{ paddingTop: '0.5px', marginLeft: '15px' }} />
);
export const TimeBasedRuleIcon = () => <Timebased style={{ marginLeft: '15px' }} />;
export const FreeRuleIcon = () => <Free style={{ marginLeft: '15px' }} />;
export const InheritedRuleIcon = () => <Inherited />;

class ChargerDetailsStore {
  automaticModeDialogOpen = false;
  rebootDialogOpen = false;
  dialogText = '';
  chargerProvider: any;

  lastHeartbeat: any;
  millisSinceLastHeartbeat: any;
  chargerOffline: any;

  activeConnector: any;
  activeDuration: any;

  openAutomaticModeDialog(text, chargerProvider) {
    this.automaticModeDialogOpen = true;
    this.rebootDialogOpen = false;
    this.dialogText = text;
    this.chargerProvider = chargerProvider;
  }

  openRebootDialog(text, chargerProvider) {
    this.rebootDialogOpen = true;
    this.automaticModeDialogOpen = false;
    this.dialogText = text;
    this.chargerProvider = chargerProvider;
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const DisplayActiveTimeDuration = observer(({ store }: { store: ChargerDetailsStore }) => (
  <NameValueRow name="duration" value={store.activeDuration} />
));

const chargerDetailsStore = new ChargerDetailsStore();

const onConfirmRebootCharger = (chargerProvider, type: 'SOFT' | 'HARD') => {
  Api.charge.resetCharger(chargerProvider.data?.id, type).then(chargerProvider.reloadResource);
};

const onConfirmToggleChargeMode = (chargerProvider, mode) => {
  Api.charge.patchCharger(chargerProvider.data?.id, { mode }).then(chargerProvider.reloadResource);
};

const AutomaticModeDialog = observer(({ chargerProvider }: { chargerProvider: any }) => {
  const closeDialog = () => {
    chargerDetailsStore.automaticModeDialogOpen = false;
  };

  return (
    <Dialog open={chargerDetailsStore.automaticModeDialogOpen} onClose={closeDialog}>
      <DialogContent>
        <Typography>{chargerDetailsStore.dialogText}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          onClick={() => {
            closeDialog();
            const newMode = chargerProvider.data?.mode === 'automatic' ? 'manual' : 'automatic';
            onConfirmToggleChargeMode(chargerProvider, newMode);
          }}
          autoFocus
          color="primary"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const RebootDialog = observer(() => {
  const [confirmType, setConfirmType] = useState<'SOFT' | 'HARD'>('SOFT');

  const closeDialog = () => {
    chargerDetailsStore.rebootDialogOpen = false;
  };

  return (
    <Dialog open={chargerDetailsStore.rebootDialogOpen} onClose={closeDialog}>
      <DialogContent>
        <Typography>{chargerDetailsStore.dialogText}</Typography>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={{ marginTop: '1em' }}>Type</Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Radio checked={confirmType === 'SOFT'} onChange={() => setConfirmType('SOFT')} />
          <Typography>Soft</Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Radio checked={confirmType === 'HARD'} onChange={() => setConfirmType('HARD')} />
          <Typography>Hard</Typography>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          onClick={() => {
            closeDialog();
            onConfirmRebootCharger(chargerDetailsStore.chargerProvider, confirmType);
          }}
          autoFocus
          color="primary"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const getDetailsRows = (chargerProvider) => {
  const newMode = chargerProvider.data?.mode === 'automatic' ? 'manual' : 'automatic';
  const actionLabel = `Set ${newMode}`;
  const actionFunction = () => {
    chargerDetailsStore.openAutomaticModeDialog(
      `Are you sure you want to set mode to ${newMode}?`,
      chargerProvider,
    );
  };

  return [
    { name: 'id', label: 'Identifier' },
    { name: 'manufacturer', label: 'Manufacturer' },
    { name: 'model', label: 'Model' },
    { name: 'ocppId', label: 'OCPP ID' },
    { name: 'rawOcppId', label: 'Raw OCPP ID' },
    { name: 'serial', label: 'Serial' },
    { name: 'mode', label: 'Mode', actionLabel, actionFunction },
    { name: 'VINAuthorization', label: 'VIN Authorization' },
  ];
};

const formatPowerKw = (value: any) => {
  if (value === undefined || value === null || value === '') return 'N/A';
  const num = Number(value) / 1000;
  if (isNaN(num)) return value;
  return `${num.toLocaleString('sl-SI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kW`;
};

const formatPowerKwRaw = (value: any) => {
  if (value === undefined || value === null || value === '' || value === 'N/A') return 'N/A';
  const num = Number(value);
  if (isNaN(num)) return value;
  return `${num.toLocaleString('sl-SI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kW`;
};

const getStatusRows = (chargerProvider) => [
  { name: 'maxPower', value: formatPowerKw(chargerProvider.data?.maxPower) },
  {
    name: 'actualMaxPower',
    label: 'Actual max power',
    value: formatPowerKwRaw(chargerProvider.data?.actualMaxPower),
  },
  { name: 'maxCurrent', label: 'Max current (A)' },
  { name: 'maxVoltage', label: 'Voltage (V)' },
  { name: 'currentFirmwareVersion', label: 'Firmware' },
  {
    name: 'status',
    actionLabel: 'reboot',
    actionFunction: () => {
      chargerDetailsStore.openRebootDialog(
        'Are you sure you want to reboot this charger?',
        chargerProvider,
      );
    },
  },
  'createdAt',
];

/* const pricingRows = [{ name: 'catalogId', label: 'priceList' }]; */
const powerSettingsFields = ['voltageFactor', 'currentFactor', 'powerFactor', 'consumptionFactor'];

const formatFactorValue = (value: any) => {
  if (value === undefined || value === null) return '';
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('sl-SI');
};

const getPowerSettingsRows = (data: any) =>
  powerSettingsFields.map((field) => ({
    name: field,
    value: formatFactorValue(data?.[field]),
  }));
/* const activityRows = [{ name: 'lastHeartbeat', label: 'lastSignal' }, 'power', 'duration', 'consumption']; */
const totalsRows = ['avgPower', 'consumption', 'time'];
const ChargerConfigurationRows = ['manufacturer', 'model'];

const buildEditDetailsInputs = () => [
  { name: 'id', label: 'identifier', type: FormInputTypes.disabledTextField },
  { name: 'manufacturer', type: FormInputTypes.textField },
  { name: 'model', type: FormInputTypes.textField },
  { name: 'serial', type: FormInputTypes.textField },
  { name: 'VINAuthorization', label: 'VIN Autorization', type: FormInputTypes.switch },
  { name: 'rawOcppId', label: 'Raw OCPP ID', type: FormInputTypes.disabledTextField },
  { name: 'ocppId', label: 'OCPP ID', type: FormInputTypes.textField },
  {
    name: 'checkOcppId',
    label: 'CHECK OCPP ID',
    type: FormInputTypes.textButton,
    alignment: 'flex-end',
    onClick: async () => {
      const ocppId = drawerStore.values?.ocppId;
      if (!ocppId) {
        return;
      }
      try {
        await Api.charge.checkChargePointAlias(ocppId, drawerStore.values?.id || null, null);
        toastStore.showMessage('OCPP ID is available', TOAST_MESSAGE_TYPE.INFO);
      } catch (err: any) {
        toastStore.showMessage(
          err?.response?.data?.error?.message || 'OCPP ID is not available',
          TOAST_MESSAGE_TYPE.ERROR,
        );
      }
    },
  },
];

const editStatusInputs = [
  { name: 'maxPower', type: FormInputTypes.textField },
  // Units live on the card rows rather than these labels: the drawer runs every label
  // through _.startCase, which would strip the brackets ("Max current (A)" -> "Max Current A").
  { name: 'maxCurrent', label: 'Max current', type: FormInputTypes.number },
  { name: 'maxVoltage', label: 'Voltage', type: FormInputTypes.number },
  { name: 'currentFirmwareVersion', label: 'firmware', type: FormInputTypes.textField },
  { name: 'createdAt', type: FormInputTypes.textField },
];

const editPowerSettingsInputs = [
  { name: 'voltageFactor', type: FormInputTypes.number },
  { name: 'currentFactor', type: FormInputTypes.number },
  { name: 'powerFactor', type: FormInputTypes.number },
  { name: 'consumptionFactor', type: FormInputTypes.number },
];

const getMilisecondDiffToNow = (startedAtISO?: string): number | undefined => {
  if (!startedAtISO) {
    return undefined;
  }

  const startedAtTime = new Date(startedAtISO).getTime();
  return Date.now() - startedAtTime;
};

const getTimeDifferenceString = (startedAtISO?: string): string => {
  const diff = getMilisecondDiffToNow(startedAtISO);
  if (!diff) {
    return '';
  }

  const duration = moment.duration(diff);
  return `${duration.hours()}:${duration.minutes()}:${duration.seconds()}`;
};

const ChargerDetailsOverview = observer(() => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  const connectorStatusStyles = useConnectorStatusStyles();

  const { chargerid } = useParams<{ chargerid: string }>();

  const [rows, setRows] = useState<any[]>([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const accountFilter = useAccountFilter();
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState<string>(today);

  const _5minsInMilliseconds = 1000 * 60 * 5;

  const chargerProviderCallback = (data) => {
    chargerDetailsStore.lastHeartbeat = data?.lastHeartbeat;

    if (chargerDetailsStore.lastHeartbeat) {
      chargerDetailsStore.millisSinceLastHeartbeat = getMilisecondDiffToNow(
        chargerDetailsStore.lastHeartbeat,
      );
    }

    chargerDetailsStore.chargerOffline =
      !chargerDetailsStore.millisSinceLastHeartbeat ||
      chargerDetailsStore.millisSinceLastHeartbeat > _5minsInMilliseconds;
  };

  const chargerActivityCallback = (data) => {
    if (data?.chargeConnectors) {
      chargerDetailsStore.activeConnector = data?.chargeConnectors[0];
    }
  };

  const chargerProvider = useProvider(
    () =>
      Api.charge
        .getCharger(chargerid)
        .catch((error) => console.error('Error fetching location catalogs:', error)),
    chargerProviderCallback,
  );
  useProvider(() => Api.charge.getChargerActivity(chargerid), chargerActivityCallback, true, 60000);
  const nameAccountsProvider = useProvider(Api.business.listAccounts, undefined, false);
  const locationsProvider = useProvider(undefined, undefined, false);
  const usersProvider = useProvider(Api.user.listUsers, undefined, false);
  const catalogsProvider = useProvider(Api.catalog.listCatalogs, undefined, false);
  const chargerCatalogsProvider = useProvider(() => Api.charge.listCatalogsForCharger(chargerid));
  const chargerAccountsProvider = useProvider(
    () => Api.charge.listAccountsForCharger(chargerid),
    undefined,
    false,
  );
  const allChargersProvider = usePaginatedProvider(undefined, undefined, false);
  const [replacementChargerOcppId, setReplacementChargerOcppId] = useState<string | null>(null);

  const locationId = chargerProvider?.data?.locationId;

  const [catalogs, setCatalogs] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const orderedCatalogs = catalogs
    .map((catalog) => {
      const parentCatalog = catalogs.find((parent) => parent.id === catalog.parentId);
      const parentName = parentCatalog ? parentCatalog.name : null;
      return { ...catalog, priority: catalog.priority || 0, parentName };
    })
    .sort((a, b) => b.priority - a.priority)
    .map((catalog, index) => ({ ...catalog, order: catalogs.length - index }));
  useEffect(() => {
    if (credentialStore.isUserAdmin) {
      usersProvider.reloadResource();
      catalogsProvider.reloadResource();
      nameAccountsProvider.reloadResource();
      chargerAccountsProvider.reloadResource();

      locationsProvider.setRequest(() => Api.charge.listLocations());
    } else {
      locationsProvider.setRequest(() =>
        Api.charge.listLocationsForAccount(credentialStore.getAccountId()),
      );
    }

    locationsProvider.reloadResource();
  }, [credentialStore.selectedAccount]);

  useEffect(() => {
    const requestFunctionForChargers = () => Api.charge.listChargers();

    allChargersProvider.setRequest(requestFunctionForChargers);

    allChargersProvider.reloadResource();
  }, [allChargersProvider]);

  const fetchCatalogData = async (catalogId) => {
    const resp = await Api.catalog.getCatalog(catalogId);
    const isInherited = await Api.charge.listCatalogsForCharger(chargerid);
    const isUserRestricted = await Api.catalog.getUserRestriction(catalogId);
    const isTimeRestricted = await Api.catalog.getTimeRestriction(catalogId);
    const isInheritedStatus = !isInherited.data.data.some((item) => item.catalogId === catalogId);

    const catalogData = {
      ...resp.data.data,
      isUserRestricted: isUserRestricted.data.data.enabled,
      isTimeRestricted: isTimeRestricted.data.data.enabled,
      isInherited: isInheritedStatus,
    };

    catalogData.priority =
      (catalogData.isUserRestricted ? 1000 : 0) +
      (catalogData.isTimeRestricted ? 100 : 0) +
      (catalogData.isFree ? 10 : 0);

    return catalogData;
  };

  const fetchAllCatalogs = async () => {
    const promises = chargerCatalogsProvider.data.map((catalog) =>
      fetchCatalogData(catalog.catalogId),
    );
    const locationCatalogs = await Promise.all(promises);
    setCatalogs(locationCatalogs);
  };

  const fetchAccountData = async (accountId) => {
    const resp = await Api.business.getAccount(accountId);
    return resp.data.data;
  };

  const fetchAllAccounts = async () => {
    const promises = chargerAccountsProvider.data.map((account) =>
      fetchAccountData(account.accountId),
    );
    const chargerAccounts = await Promise.all(promises);
    setAccounts(chargerAccounts);
  };

  const fetchLocationCatalogs = async () => {
    if (!locationId) return;
    try {
      const response = await Api.charge.listCatalogsForLocation(locationId);
      const promises = response.data.data.map((catalog) => fetchCatalogData(catalog.catalogId));
      const locationCatalogs = await Promise.all(promises);
      setCatalogs(locationCatalogs);
    } catch (error) {
      console.error('Error fetching location catalogs:', error);
    }
  };

  useEffect(() => {
    if (chargerProvider.data) {
      chargerCatalogsProvider.reloadResource();
    }
  }, [chargerProvider.data]);

  useEffect(() => {
    if (chargerCatalogsProvider.data === undefined) {
      return;
    }

    if (chargerCatalogsProvider.data.length > 0) {
      fetchAllCatalogs();
    } else if (locationId) {
      fetchLocationCatalogs();
    }
  }, [chargerCatalogsProvider.data, locationId]);

  useEffect(() => {
    if (chargerAccountsProvider.data !== undefined) {
      fetchAllAccounts();
    }
  }, [chargerAccountsProvider.data]);

  useEffect(() => {
    if (credentialStore.isUserAdmin && chargerProvider.data?.replacedBy) {
      Api.charge
        .getCharger(chargerProvider.data.replacedBy)
        .then((response) => {
          setReplacementChargerOcppId(response.data.data.ocppId);
        })
        .catch((error) => {
          console.error('Error fetching charger data:', error);
        });
    }
  }, [credentialStore.isUserAdmin, chargerProvider.data?.replacedBy]);

  const confirmAddCatalogToCharger = (values) => {
    Api.catalog.getCatalog(values.catalogId).then((data) => {
      const childrenIds = data?.data?.data?.children.map((child) => child.id);
      Api.charge
        .addCatalogToCharger(chargerid, values.catalogId, childrenIds)
        .then(chargerCatalogsProvider.reloadResource);
    });
  };

  const onClickAddCatalogToCharger = () => {
    drawerStore.openDrawer({
      label: 'Add price list',
      inputs: [
        getPriceListDropdownInput(
          catalogsProvider,
          chargerCatalogsProvider.data.map((el) => el.catalogId),
        ),
      ],
      clickCallback: () => confirmAddCatalogToCharger(toJS(drawerStore.values)),
    });
  };

  const onClickRemoveCatalogFromCharger = (catalogId) => {
    const childrenIds = orderedCatalogs
      .filter((catalog) => catalog.parentId === catalogId)
      .map((catalog) => catalog.id);

    const catalogIds = [...childrenIds, catalogId];

    Api.charge
      .removeCatalogFromCharger(chargerid, catalogIds)
      .then(chargerCatalogsProvider.reloadResource);
  };

  const confirmAssignToAccount = () => {
    Api.charge
      .addAccountToCharger(chargerid, drawerStore.values.accountId)
      .then(chargerAccountsProvider.reloadResource);
  };

  const onClickAssignedTo = () => {
    drawerStore.openDrawer({
      label: 'Assign to account',
      inputs: assignToAccountInputs(nameAccountsProvider, chargerAccountsProvider),
      clickCallback: confirmAssignToAccount,
    });
  };

  const onClickQRCodeIcon = (connector) => {
    drawerStore.openDrawer({
      label: 'Charge connector',
      inputs: [{ name: 'ocppId', type: FormInputTypes.qrCode }],
      clickCallback: undefined,
      buttonLabel: 'Close',
    });
    drawerStore.setValues(connector);
  };

  const onClickStartConnector = (connector) => {
    const startFn = credentialStore.isUserAdmin
      ? Api.charge.startConnector
      : Api.charge.startConnectorAsOwner;
    startFn(connector.id).then(chargerProvider.reloadResource);
  };

  const onClickStopConnector = (connector) => {
    const stopFn = credentialStore.isUserAdmin
      ? Api.charge.stopConnector
      : Api.charge.stopConnectorAsOwner;
    stopFn(connector.id).then(chargerProvider.reloadResource);
  };

  const onClickConnectorSettings = (connector: any) => {
    drawerStore.openDrawer({
      label: 'Connector settings',
      inputs: [
        { name: 'rawOcppId', label: 'Raw OCPP ID', type: FormInputTypes.disabledTextField },
        { name: 'ocppId', label: 'OCPP ID', type: FormInputTypes.textField },
        {
          name: 'checkConnectorOcppId',
          label: 'CHECK OCPP ID',
          type: FormInputTypes.textButton,
          alignment: 'flex-end',
          onClick: async () => {
            const ocppId = drawerStore.values?.ocppId;
            if (!ocppId) {
              return;
            }
            try {
              await Api.charge.checkChargePointAlias(
                ocppId,
                null,
                drawerStore.values?.connectorId || null,
              );
              toastStore.showMessage('OCPP ID is available', TOAST_MESSAGE_TYPE.INFO);
            } catch (err: any) {
              toastStore.showMessage(
                err?.response?.data?.error?.message || 'OCPP ID is not available',
                TOAST_MESSAGE_TYPE.ERROR,
              );
            }
          },
        },
        { name: 'isHidden', label: 'Is Hidden', type: FormInputTypes.switch },
        { name: 'isNapFinanced', label: 'NAP financed', type: FormInputTypes.switch },
      ],
      saveWithoutReset: true,
      clickCallback: async () => {
        const values = toJS(drawerStore.values);
        if (values.ocppId && values.ocppId !== values.originalOcppId) {
          try {
            await Api.charge.checkChargePointAlias(values.ocppId, null, values.connectorId || null);
          } catch (err: any) {
            toastStore.showMessage(
              err?.response?.data?.error?.message || 'OCPP ID already exists',
              TOAST_MESSAGE_TYPE.ERROR,
            );
            drawerStore.setSaveWithoutReset(true);
            return;
          }
          await Api.charge.updateConnector(values.connectorId, { ocppId: values.ocppId });
        }
        if (!!values.isHidden !== !!values.originalIsHidden) {
          await Api.charge.updateConnector(values.connectorId, { isHidden: !!values.isHidden });
        }
        if (!!values.isNapFinanced !== !!values.originalIsNapFinanced) {
          await Api.charge.updateConnector(values.connectorId, {
            isNapFinanced: !!values.isNapFinanced,
          });
        }
        chargerProvider.reloadResource();
        drawerStore.reset();
      },
      buttonLabel: 'SAVE',
    });
    drawerStore.setValues({
      connectorId: connector.id,
      rawOcppId: connector.rawOcppId || connector.ocppId,
      ocppId: connector.ocppId,
      originalOcppId: connector.ocppId,
      isHidden: !!connector.isHidden,
      originalIsHidden: !!connector.isHidden,
      isNapFinanced: !!connector.isNapFinanced,
      originalIsNapFinanced: !!connector.isNapFinanced,
    });
  };

  const connectorActions = (params: GridRowParams) => (
    <ActionsList
      actions={[
        {
          label: 'Start charging',
          callback: () => {
            onClickStartConnector(params.row);
          },
        },
        {
          label: 'Stop charging',
          callback: () => {
            onClickStopConnector(params.row);
          },
        },
        {
          label: 'Settings',
          callback: () => {
            onClickConnectorSettings(params.row);
          },
        },
      ]}
    />
  );

  const connectorColumns = [
    gridColumn('occpID', {
      flex: 4,
      headerName: 'OCPP ID',
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const displayId = params.row.ocppId;
        return (
          <div style={{ display: 'flex', alignItems: 'center', alignContent: 'center' }}>
            <Typography>{displayId}</Typography>
            <IconButton
              onClick={() => {
                copyTextToClipboard(displayId);
              }}
            >
              <ContentCopyIcon
                style={{ marginLeft: '0.3em', color: greenTextColor, fontSize: 18 }}
              />
            </IconButton>
          </div>
        );
      },
    }),
    gridColumn('identifier', {
      flex: 3,
      headerName: 'Name',
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography style={{ marginRight: 16 }}>{params.row.name}</Typography>
        </div>
      ),
    }),
    gridColumn('connector', {
      flex: 3,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <ConnectorColumnCell connector={params.row} showLink={false} />
      ),
    }),
    gridColumn('status', {
      flex: 3,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <ConnectorStatusBadge status={params.row.status} />,
    }),
    // Visibility column only appears on chargers that actually have a hidden
    // connector — hiding a connector is a rare operator action, and rendering
    // the column on every charger with an empty cell everywhere just adds
    // visual noise.
    ...(chargerProvider?.data?.connectors?.some((c: any) => c.isHidden)
      ? [
          gridColumn('visibility', {
            flex: 2,
            minWidth: 110,
            headerName: 'Visibility',
            renderCell: (params: GridRowParams) =>
              params.row.isHidden ? (
                <ColumnStatusBadge label="HIDDEN" color="#9ca3af" textColor="#fff" />
              ) : null,
          }),
        ]
      : []),
    gridColumn('instantPower', {
      headerName: 'Active power',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params?.row?.instantPower || 0}</div>,
    }),
    gridColumn('consumption', {
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params?.row?.consumption || 0}</div>,
    }),
    gridColumn('time', {
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => <div>{params?.row?.time || 0}</div>,
    }),
    gridColumn('qr', {
      width: 60,
      headerName: 'QR',
      renderCell: (params: GridRowParams) => (
        <IconButton onClick={() => onClickQRCodeIcon(params.row)} style={{ padding: 0 }}>
          <QrCodeIcon color="primary" />
        </IconButton>
      ),
    }),
    gridColumn(' ', {
      width: 60,
      align: 'center',
      headerAlign: 'center',
      headerName: ' ',
      renderCell: (params: GridRowParams) => connectorActions(params),
    }),
  ];

  const formatItemTime = (item, index, rowLength) => {
    if (index !== 0) {
      item.startedAt = moment.utc(item.startedAt, 'HH:mm').local().format('HH:mm');
    }
    if (index !== rowLength - 1) {
      item.endedAt = moment.utc(item.endedAt, 'HH:mm').local().format('HH:mm');
    }
  };

  const fillMissingHours = (mappedDayItem, lastHour) => {
    for (let hour = 0; hour <= lastHour; hour++) {
      if (!mappedDayItem.hourValues[hour]) {
        mappedDayItem.hourValues[hour] = [];
      }
    }
  };

  const fillRemainingHours = (mappedDayItem, lastItem, isToday) => {
    const startHour = parseInt(moment(lastItem.startedAt, 'HH:mm').format('HH'), 10) + 1;
    const endHour = isToday ? moment().hour() + 1 : 24;

    for (let hour = startHour; hour < endHour; hour++) {
      if (!mappedDayItem.hourValues[hour]) {
        mappedDayItem.hourValues[hour] = [];
      }
      mappedDayItem.hourValues[hour].push({
        hour,
        ...lastItem,
      });
    }
  };

  const mapRowItemsToHourValues = (row, mappedDayItem) => {
    let lastHour = -1;

    row.items?.forEach((item, index) => {
      const startHour = parseInt(item.startedAt.split(':')[0], 10);
      if (!mappedDayItem.hourValues[startHour]) {
        mappedDayItem.hourValues[startHour] = [];
      }

      mappedDayItem.hourValues[startHour].push({
        hour: startHour,
        ...item,
      });

      lastHour = Math.max(lastHour, startHour);

      if (index !== row.items.length - 1) {
        const nextStartHour = parseInt(row.items[index + 1].startedAt.split(':')[0], 10);
        for (let hour = startHour + 1; hour < nextStartHour; hour++) {
          if (!mappedDayItem.hourValues[hour]) {
            mappedDayItem.hourValues[hour] = [];
            mappedDayItem.hourValues[hour].push({
              hour,
              ...item,
            });
          }
        }
      }
    });

    return lastHour;
  };

  const setMappedDataFromResponse = (res) => {
    const resRows = res.items;

    const newMappedData = [];
    const isToday = moment(res.date).isSame(moment(), 'day');

    resRows?.forEach((row) => {
      const mappedDayItem = {
        connectorId: row.connectorId,
        hourValues: new Map(),
        items: row.items,
      };

      row.items?.forEach((item, index) => formatItemTime(item, index, row.items.length));

      if (isToday && row.items?.length > 0) {
        const lastItem = row.items[row.items.length - 1];
        lastItem.endedAt = moment.utc(lastItem.endedAt, 'HH:mm').local().format('HH:mm');
      }

      const lastHour = mapRowItemsToHourValues(row, mappedDayItem);

      fillMissingHours(mappedDayItem, lastHour);

      if (row.items?.length > 0) {
        const lastItem = row.items[row.items.length - 1];
        fillRemainingHours(mappedDayItem, lastItem, isToday);
      }

      newMappedData.push(mappedDayItem);
    });

    setMappedData(newMappedData);
  };

  const loadStatItems = async () => {
    try {
      const params = getCommonParams(accountFilter);
      const res = await Api.charge.getChargerWithParams(chargerid, params);
      const connectors = res.data?.data?.connectors;
      if (connectors && connectors.length > 0) {
        const resconnector = await Api.charge.getConnectorsStatus(connectors, date);
        const data = resconnector.data?.data;
        if (data) {
          const items = data;
          const count = data.length;
          const formattedResponse = {
            data: {
              date,
              count,
              items,
            },
          };
          setMappedDataFromResponse(formattedResponse.data);
        }
      }
    } catch (error) {
      console.error('Error loading connector statuses:', error);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      chargerDetailsStore.activeDuration = getTimeDifferenceString(
        chargerDetailsStore.activeConnector?.startedAt,
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, [chargerDetailsStore.activeConnector?.startedAt]);

  const onClickRemoveAccountFromCharger = (accountId: string) => {
    Api.charge
      .removeAccountFromCharger(chargerid, accountId)
      .then(chargerAccountsProvider.reloadResource);
  };

  const handleRowClick = (params) => {
    setEditingRowId(params.id);
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
  };

  const getRows = () => {
    const newRows = [];

    mappedData.forEach((rowItem) => {
      const newRow = {
        id: rowItem.connectorId,
        connectorId: rowItem.connectorId,
        items: rowItem.items,
        hourValues: rowItem.hourValues,
      };
      newRows.push(newRow);
    });

    return newRows;
  };

  useEffect(() => {
    loadStatItems();
  }, [date, chargerProvider.data]);

  useEffect(() => {
    setRows(getRows());
  }, [mappedData]);

  const getItemColumns = () => {
    const columns = [
      gridColumn('ocpp id', {
        flex: 4,
        minWidth: 100,
        renderCell: (params: GridRowParams) => {
          const { connectorId } = params.row;
          // Resolve alias from connector data
          const connector = chargerProvider.data?.connectors?.find(
            (c: any) => c.ocppId === connectorId || c.id === connectorId,
          );
          const displayId = connectorId;
          return (
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayId}
            </div>
          );
        },
      }),
    ];

    for (let i = 0; i <= 23; i++) {
      columns.push(
        gridColumn(`hour_${i}`, {
          flex: 1,
          minWidth: 10,
          headerName: `${i}`,
          textAlign: 'left',
          hide: false,
          renderCell: (params: GridRowParams) => {
            const hourValues = params.row.hourValues[i] || [];

            if (hourValues.length === 1) {
              const statusValue = hourValues[0]?.status || null;
              const colorLookup = {
                [CONNECTOR_STATUS.MAINTENANCE]: '#d9b114',
                [CONNECTOR_STATUS.OFFLINE]: 'lightgrey',
                [CONNECTOR_STATUS.ERROR]: 'red',
                [CONNECTOR_STATUS.AVAILABLE]: '#3a3a3a',
                [CONNECTOR_STATUS.IN_USE]: 'green',
                [CONNECTOR_STATUS.READY_TO_CHARGE]: '#14b8d9',
                default: 'white',
              };
              const color = colorLookup[statusValue] || colorLookup.default;

              const tooltipContent = (
                <Typography variant="body1">
                  <strong>{statusValue?.toUpperCase()}</strong> <br />
                  {moment.utc(hourValues[0].startedAt, 'HH:mm').format('HH:mm')}-
                  {moment.utc(hourValues[0].endedAt, 'HH:mm').format('HH:mm')}
                </Typography>
              );

              return (
                <Tooltip title={tooltipContent} enterDelay={100} enterNextDelay={100}>
                  <div
                    style={{
                      height: '80%',
                      width: '100%',
                      display: 'flex',
                      padding: '0px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: color,
                    }}
                    onClick={() => handleRowClick(params)}
                  />
                </Tooltip>
              );
            }

            return (
              <div style={{ height: '80%', width: '100%', display: 'flex', padding: '0px' }}>
                {hourValues.slice(0, 5).map((item, index) => {
                  const totalItems = Math.min(hourValues.length, 5);
                  const widthPercentage = totalItems > 0 ? 100 / totalItems : 0;

                  const colorLookup = {
                    [CONNECTOR_STATUS.MAINTENANCE]: '#d9b114',
                    [CONNECTOR_STATUS.OFFLINE]: 'lightgrey',
                    [CONNECTOR_STATUS.ERROR]: 'red',
                    [CONNECTOR_STATUS.AVAILABLE]: '#3a3a3a',
                    [CONNECTOR_STATUS.IN_USE]: 'green',
                    ready_to_charge: '#14b8d9',
                    default: 'white',
                  };

                  const color = colorLookup[item.status] || colorLookup.default;
                  const tooltipContent = (
                    <div>
                      {hourValues.map((fullItem, fullIndex) => (
                        <Typography key={fullIndex} variant="body2" style={{ marginBottom: '4px' }}>
                          <strong>{fullItem.status.toUpperCase()}</strong> <br />
                          {fullItem.startedAt} - {fullItem.endedAt}
                        </Typography>
                      ))}
                    </div>
                  );

                  return (
                    <Tooltip
                      key={index}
                      title={tooltipContent}
                      enterDelay={100}
                      enterNextDelay={100}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${widthPercentage}%`,
                          display: 'flex',
                          padding: '0px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: color,
                        }}
                        onClick={() => handleRowClick(params)}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            );
          },
        }),
      );
    }
    return columns;
  };

  const confirmEditDetails = async (values) => {
    // Validate OCPP ID uniqueness before saving
    if (values.ocppId && values.ocppId !== values.rawOcppId) {
      try {
        await Api.charge.checkChargePointAlias(values.ocppId, chargerid, null);
      } catch (err: any) {
        toastStore.showMessage(
          err?.response?.data?.error?.message || 'OCPP ID already exists',
          TOAST_MESSAGE_TYPE.ERROR,
        );
        // Keep saveWithoutReset true for next attempt
        drawerStore.setSaveWithoutReset(true);
        return;
      }
    }
    await Api.charge.patchCharger(chargerid, values);
    chargerProvider.reloadResource();
    drawerStore.reset();
  };

  const confirmEditStatus = async (values) => {
    // chargePoint.maxPower is stored in Watts on the backend. The edit form
    // shows and takes kW (input "44" means 44 kW), so convert on the way out.
    const payload = { ...values };
    if (payload.maxPower !== undefined && payload.maxPower !== null && payload.maxPower !== '') {
      const kw = Number(payload.maxPower);
      if (!Number.isNaN(kw)) {
        payload.maxPower = kw * 1000;
      }
    }
    // Blank numeric fields must be omitted, not sent: the API validates them as
    // numbers and rejects '' and null with a 400, which would fail the whole PATCH
    // and take maxPower and firmware down with it. Omitting leaves the stored value.
    ['maxCurrent', 'maxVoltage'].forEach((key) => {
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    await Api.charge.patchCharger(chargerid, payload);
    chargerProvider.reloadResource();
  };

  const confirmEditPowerSettings = async (values) => {
    await Api.charge.patchCharger(chargerid, values);
    chargerProvider.reloadResource();
  };

  const confirmAssignCharger = (chargerId, values) => {
    if (values.userId) {
      Api.charge
        .assignChargerToUser(chargerId, values.userId, values.identifier)
        .then(chargerProvider.reloadResource);
    } else if (values.locationId) {
      Api.charge
        .assignChargerToLocation(chargerId, values.locationId, values.identifier)
        .then(chargerProvider.reloadResource);
    }
  };

  const onClickEditDetails = () => {
    drawerStore.openDrawer({
      label: 'Edit charge point',
      inputs: buildEditDetailsInputs(),
      buttonLabel: 'SAVE CHARGE POINT',
      saveWithoutReset: true,
      clickCallback: () => confirmEditDetails(toJS(drawerStore.values)),
    });
    drawerStore.setValues(chargerProvider.data);
  };

  const onClickEditStatus = () => {
    drawerStore.openDrawer({
      label: 'Edit charge point status',
      inputs: editStatusInputs,
      clickCallback: () => confirmEditStatus(toJS(drawerStore.values)),
    });
    // Show maxPower in kW inside the input to match the label; confirmEditStatus
    // converts back to Watts on save.
    const source = chargerProvider.data || {};
    const rawMaxPower = source.maxPower;
    const maxPowerKw =
      rawMaxPower !== undefined && rawMaxPower !== null && rawMaxPower !== ''
        ? Number(rawMaxPower) / 1000
        : rawMaxPower;
    drawerStore.setValues({ ...source, maxPower: maxPowerKw });
  };

  const onClickDisplayChargerConfiguration = async () => {
    if (chargerid === undefined) {
      return;
    }

    const chargePointConfigurationId = (
      await Api.charge.getChargePointConfigurationRequest({ chargePointId: chargerid })
    ).data;
    if (
      chargePointConfigurationId &&
      Array.isArray(chargePointConfigurationId.data) &&
      chargePointConfigurationId.data.length > 0
    ) {
      const { configurationId } = chargePointConfigurationId.data[0];
      const newOperationConfig = (
        await Api.charge.getConfigurationOperationRequest({ id: configurationId })
      ).data;

      const { settings } = newOperationConfig.data[0];
      drawerStore.openDrawer({
        label: 'Charger configuration',
        inputs: [{ name: 'chargeConfig', type: FormInputTypes.jsonView, value: settings }],
      });
      drawerStore.setValues(settings);
    } else {
      drawerStore.openDrawer({
        label: 'Charger configuration',
        inputs: [{ label: '', name: 'chargeConfig', type: FormInputTypes.textView, value: 'N/A' }],
      });
    }
  };

  const onClickEditPowerSettings = () => {
    drawerStore.openDrawer({
      label: 'Edit charge point power settings',
      inputs: editPowerSettingsInputs,
      clickCallback: () => confirmEditPowerSettings(toJS(drawerStore.values)),
    });
    drawerStore.setValues(chargerProvider.data);
  };

  const onClickAssignCharger = () => {
    drawerStore.openDrawer({
      label: 'Assign charge point',
      inputs: getAssignChargerCombinedInputs(locationsProvider, usersProvider),
      clickCallback: () => confirmAssignCharger(chargerProvider.data?.id, toJS(drawerStore.values)),
    });
  };

  const getChargerOptions = () =>
    allChargersProvider?.data?.map((charger) => {
      const label = `${charger.manufacturer} / ${charger.model}`;
      return {
        topRow: label,
        bottomRow: charger.ocppId,
        optionName: charger.id,
        label,
      };
    }) || [];

  const sendCommandInputs = [
    {
      id: 'selectedSendCommand',
      label: 'Apply Option',
      name: 'selectedSendCommand',
      type: FormInputTypes.radioButton,
      options: [
        {
          label: 'BOOT NOTIFICATION',
          value: 'BootNotification',
          description:
            'Requests a boot notification from the charger. Make sure that session is not running.',
        },
        {
          label: 'HEARTBEAT',
          value: 'Heartbeat',
          description:
            'Requests a heartbeat from the charger. Make sure that session is not running.',
        },
        {
          label: 'METER VALUES',
          value: 'MeterValues',
          description: 'Requests an update about the energy consumption meter and current power.',
        },
        {
          label: 'STATUS NOTIFICATION',
          value: 'StatusNotification',
          description: 'Requests a status update from the charger.',
        },
        {
          label: 'UNLOCK CONNECTOR',
          value: 'UnlockConnector',
          description: 'Requests to unlock the connector.',
        },
        {
          label: 'CURRENT OUTPUT POWER',
          value: 'CurrentOutputPower',
          description: 'Sets the current output power based on the given input.',
          renderExtra: (isSelected, processedValues, updateInputValue) =>
            isSelected && (
              <TextField
                label="Output Power (kW)"
                variant="outlined"
                size="small"
                style={{ marginLeft: '2rem', marginTop: '0.5rem' }}
                value={processedValues['outputPowerValue'] || ''}
                onChange={(e) => updateInputValue(e.target.value, 'outputPowerValue')}
              />
            ),
        },
      ],
    },
  ];

  const replacingChargePoint = allChargersProvider?.data?.find(
    (chargePoint) => chargePoint.replacedBy === chargerProvider.data?.id,
  );

  const replaceCommandInputs = [
    { name: 'replacementDate', type: FormInputTypes.datePicker, defaultDate: moment().toDate() },
    {
      name: 'chargePoint',
      label: 'Charge Point',
      type: FormInputTypes.multilineSelect,
      options: getChargerOptions(),
    },
    { name: 'note', type: FormInputTypes.textField },
  ];

  const saveSendCommand = () => {
    const drawerStoreValues = toJS(drawerStore.values);
    console.log('drawerStoreValues', drawerStoreValues);

    const payload = {
      command: drawerStoreValues?.selectedSendCommand,
      ...(drawerStoreValues?.outputPowerValue && { value: drawerStoreValues.outputPowerValue }),
    };

    Api.charge.triggerCommand(chargerProvider.data?.id, payload);
  };

  const saveReplaceCommand = async () => {
    const drawerStoreValues = toJS(drawerStore.values);
    try {
      await Api.charge.replaceChargePointWithId(chargerid, drawerStoreValues);
      chargerProvider.reloadResource();
    } catch (error) {
      console.error('Error replacing charge point:', error);
    }
  };

  const onClickUndoReplacement = async () => {
    const isConfirmed = window.confirm('Are you sure you want to undo the replacement?');

    if (!isConfirmed) {
      return; // Exit if the user clicks "No"
    }

    try {
      const undoId = replacingChargePoint ? replacingChargePoint.id : chargerid;
      await Api.charge.undoReplaceChargePointWithId(undoId);
      allChargersProvider.reloadResource();
      chargerProvider.reloadResource();
    } catch (error) {
      console.error('Error undoing charge point replacement:', error);
    }
  };

  const onClickSendCommand = () => {
    drawerStore.openDrawer({
      label: 'Send command',
      inputs: sendCommandInputs,
      clickCallback: saveSendCommand,
      buttonLabel: 'Send',
    });
    drawerStore.setValues({ selectedSendCommand: 'BootNotification' });
  };

  const onClickReplaceCommand = () => {
    drawerStore.openDrawer({
      label: 'Replace command',
      inputs: replaceCommandInputs,
      clickCallback: saveReplaceCommand,
    });
  };

  return (
    <div className={connectorStatusStyles.root} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1em' }}>
        <Button variant="text" color="primary" onClick={onClickSendCommand}>
          Send Command
        </Button>
        {chargerProvider?.data?.status === CHARGER_STATUS.UNASSIGNED && (
          <Button variant="text" color="primary" onClick={onClickAssignCharger}>
            Assign
          </Button>
        )}
        <Button variant="text" color="primary" onClick={onClickReplaceCommand}>
          Replace
        </Button>
      </div>
      <div className={classes.cardRows}>
        <div className={classes.leftCardRow}>
          <StyledCard>
            <StyledCardHeader
              title="Details"
              onClick={onClickEditDetails}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList
                rows={getDetailsRows(chargerProvider)}
                values={chargerProvider.data}
              />
            </StyledCardContent>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader
              title="Status"
              onClick={onClickEditStatus}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList
                rows={getStatusRows(chargerProvider)}
                values={{
                  ...chargerProvider.data,
                  createdAt: formatToSloDate(chargerProvider.data?.createdAt),
                }}
              />
            </StyledCardContent>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader title="Location" />
            <StyledCardContent>
              {chargerProvider.data?.locationId ? (
                <NameValueRow
                  name="Name"
                  value={chargerProvider.data?.locationName || chargerProvider.data?.locationId}
                  linkTo={`/locations/${chargerProvider.data.locationId}/Overview`}
                />
              ) : (
                <NameValueRow name="Name" value="No location assigned" />
              )}
            </StyledCardContent>
          </StyledCard>
          <StyledCard>
            <StyledCardHeader
              title="Charger Configuration"
              onClick={onClickDisplayChargerConfiguration}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList rows={ChargerConfigurationRows} values={chargerProvider.data} />
            </StyledCardContent>
          </StyledCard>
        </div>

        <div className={classes.rightCardRow}>
          <StyledCard>
            <StyledCardHeader title="Activity" />
            <StyledCardContent>
              <div className={cardClasses.cardRow}>
                <Typography
                  className={cardClasses.cardRowLeftItem}
                  style={{ textTransform: 'uppercase' }}
                >
                  Last signal
                </Typography>
                <div
                  className={cardClasses.cardRowRightItem}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography>{formatToSloDate(chargerDetailsStore.lastHeartbeat)}</Typography>
                  {chargerDetailsStore.chargerOffline && (
                    <ColumnStatusBadge label="OFFLINE" color="#00E8A8" />
                  )}
                </div>
              </div>
              <NameValueRow name="power" value={chargerDetailsStore.activeConnector?.power} />
              <DisplayActiveTimeDuration store={chargerDetailsStore} />
              <NameValueRow
                name="consumption"
                value={chargerDetailsStore.activeConnector?.consumption}
              />
            </StyledCardContent>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader
              title="Power settings"
              onClick={onClickEditPowerSettings}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList
                rows={getPowerSettingsRows(chargerProvider.data)}
                values={chargerProvider.data}
              />
            </StyledCardContent>
          </StyledCard>

          <ChargerNapSiSettingsCard chargePointId={chargerid} />

          <StyledCard>
            <StyledCardHeader title="Totals" />
            <StyledCardContent>
              <NameValueRowList rows={totalsRows} values={chargerProvider.data} />
            </StyledCardContent>
          </StyledCard>

          {credentialStore.isUserAdmin && (
            <StyledCard>
              <StyledCardHeader
                title="Assigned to"
                onClick={onClickAssignedTo}
                headerButtonIcon={HeaderButtonIcon.add}
              />
              <StyledCardContent>
                {accounts?.map((account) => (
                  <TitleSubtitleRow
                    key={account.id}
                    title={account.name}
                    subtitle={account.email}
                    actions={
                      <ActionsList
                        actions={[
                          {
                            label: 'Remove',
                            callback: () => onClickRemoveAccountFromCharger(account.id),
                          },
                        ]}
                      />
                    }
                    titleLink={companyDetailsParameters(account.id)}
                  />
                ))}
              </StyledCardContent>
            </StyledCard>
          )}
          {credentialStore.isUserAdmin && chargerProvider.data?.replacedBy && (
            <StyledCard>
              <StyledCardHeader
                title="Replaced By"
                onClick={onClickUndoReplacement}
                headerButtonIcon={HeaderButtonIcon.undo}
              />
              <StyledCardContent>
                <NameValueRow name="Id" value={chargerProvider.data?.replacedBy || 'N/A'} />
                <NameValueRow name="OcppId" value={replacementChargerOcppId || 'N/A'} />
                <NameValueRow
                  name="Replacement Date"
                  value={
                    chargerProvider.data?.replacedDate
                      ? new Date(chargerProvider.data?.replacedDate).toLocaleDateString('en-GB')
                      : 'N/A'
                  }
                />
                <NameValueRow name="Note" value={chargerProvider.data?.replacedNote || ''} />
                <NameValueRow
                  name="Replacement Status"
                  value={chargerProvider.data?.replacedStatus || 'N/A'}
                />
              </StyledCardContent>
            </StyledCard>
          )}
          {credentialStore.isUserAdmin && replacingChargePoint && (
            <StyledCard>
              <StyledCardHeader
                title="Replaced to"
                onClick={onClickUndoReplacement}
                headerButtonIcon={HeaderButtonIcon.undo}
              />
              <StyledCardContent>
                <NameValueRow name="Id" value={replacingChargePoint?.id || 'N/A'} />
                <NameValueRow name="OcppId" value={replacingChargePoint.ocppId || 'N/A'} />
                <NameValueRow
                  name="Replacement Date"
                  value={
                    replacingChargePoint?.replacedDate
                      ? new Date(replacingChargePoint.replacedDate).toLocaleDateString('en-GB')
                      : 'N/A'
                  }
                />
                <NameValueRow name="Note" value={replacingChargePoint?.replacedNote || ''} />
                <NameValueRow
                  name="Replacement Status"
                  value={replacingChargePoint?.replacedStatus || 'N/A'}
                />
              </StyledCardContent>
            </StyledCard>
          )}
        </div>
      </div>

      <VMDataGrid
        rows={chargerProvider?.data?.connectors}
        columns={connectorColumns}
        label="Connectors"
      />
      <br />
      <div className={connectorStatusStyles.root}>
        <VMDataGrid
          rows={rows}
          columns={getItemColumns()}
          label="Connector status"
          pageSize={60}
          onDateChangeCallBack={handleDateChange}
          date={date}
        />
      </div>

      <AutomaticModeDialog chargerProvider={chargerProvider} />
      <RebootDialog />
    </div>
  );
});

export default ChargerDetailsOverview;
